import axios from 'axios';

import { apiClient } from './client';

// Speglar verdict.schema.ts i backenden. Ett nytt utfall eller en ny rotorsak där
// måste in här också, annars filtreras de bort ur URL:en som okända.
export const OUTCOMES = [
  'resolved',
  'necessary_handoff',
  'unnecessary_handoff',
  'miss',
  'abandoned',
  'not_applicable',
] as const;

export const ROOT_CAUSES = [
  'knowledge_missing',
  'knowledge_ignored',
  'search_miss',
  'tool_result_ignored',
  'wrong_information',
  'prompt_behavior',
  'product_data_missing',
  'flow_friction',
  'needs_human',
] as const;

export type JudgeOutcome = (typeof OUTCOMES)[number];
export type JudgeRootCause = (typeof ROOT_CAUSES)[number];

export interface JudgePeriod {
  basis: 'source_created_at' | 'updated_at';
  start: string;
  end: string;
  fromDate: string;
  toDate: string;
}

export interface JudgeDay {
  date: string;
  total: number;
  byOutcome: Record<JudgeOutcome, number>;
}

export interface JudgePattern {
  rootCause: string;
  isoYear: number;
  isoWeek: number;
  weekFrom: string;
  weekTo: string;
  currentCount: number;
  alertedAt: string | null;
  alertedCount: number | null;
  exampleSessionIds: string[];
}

export interface JudgeSummaryResponse {
  period: JudgePeriod;
  promptVersion: string;
  promptVersions: string[];
  totals: {
    judged: number;
    resolved: number;
    resolvedRate: number | null;
    notApplicable: number;
  };
  byOutcome: Array<{ outcome: JudgeOutcome; count: number }>;
  byRootCause: Array<{ rootCause: JudgeRootCause; count: number }>;
  daily: JudgeDay[];
  patterns: JudgePattern[];
}

export interface JudgeVerdictItem {
  sessionId: string;
  sourceCreatedAt: string | null;
  judgedAt: string;
  intent: string | null;
  customerGoal: string | null;
  outcome: string | null;
  rootCause: string | null;
  missingForBot: string | null;
  quote: string | null;
}

export interface JudgeVerdictsResponse {
  items: JudgeVerdictItem[];
  nextCursor: string | null;
  total: number;
}

export interface JudgePeriodParams {
  from?: string;
  to?: string;
  judgedFrom?: string;
  judgedTo?: string;
  promptVersion?: string;
}

export interface JudgeVerdictsParams extends JudgePeriodParams {
  outcome?: string;
  rootCause?: string;
  limit?: number;
  cursor?: string;
}

export const getJudgeSummary = async (params: JudgePeriodParams): Promise<JudgeSummaryResponse> => {
  const { data } = await apiClient.get<JudgeSummaryResponse>('/judge/summary', { params });
  return data;
};

export const listJudgeVerdicts = async (
  params: JudgeVerdictsParams,
): Promise<JudgeVerdictsResponse> => {
  const { data } = await apiClient.get<JudgeVerdictsResponse>('/judge/verdicts', { params });
  return data;
};

export interface JudgeEvidence {
  turn: number;
  quote: string;
}

export interface SessionVerdictRow {
  promptVersion: string;
  status: 'succeeded' | 'failed';
  judgedAt: string;
  judgeModel: string | null;
  attempts: number;
  failure: {
    kind: 'transient' | 'permanent' | null;
    errorCode: string | null;
    errorMessage: string | null;
    hardFailedAt: string | null;
  } | null;
  verdict: {
    intent: string;
    customerGoal: string;
    outcome: string;
    rootCause: string | null;
    couldBotHaveSolved: boolean;
    missingForBot: string | null;
    gaveNextStep: boolean;
    evidence: JudgeEvidence[];
    factualErrors: JudgeEvidence[];
    reasoning: string;
  } | null;
}

export interface SessionVerdictResponse {
  sessionId: string;
  promptVersion: string;
  requested: SessionVerdictRow | null;
  latestSucceeded: SessionVerdictRow | null;
}

// 404 betyder att domaren inte har tittat på sessionen än, inte att något är fel.
export const getSessionVerdict = async (
  sessionId: string,
): Promise<SessionVerdictResponse | null> => {
  try {
    const { data } = await apiClient.get<SessionVerdictResponse>(
      `/judge/verdicts/${encodeURIComponent(sessionId)}`,
    );
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
};
